import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { URLS } from '../../helpers/URLS';
import { Button, Dialog, DialogActions, DialogContent, TextField, IconButton, Tooltip } from '@mui/material';
import useAxios from '../../helpers/Hooks/useAxios';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import WcOutlinedIcon from '@mui/icons-material/WcOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import ProfilePhotoModal from "./ProfilePhotoModal";
import { downloadLink } from "../../helpers/constFIles";
import UserProfileSkills from "./UserProfileSkills";
import UserAssetsDetails from "./UserAssetsDetails";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setProfilePic } from "../../Redux/reducer";
import '../../assets/scss/tables.scss';
import './Profile.scss'

const initials = (name) => {
    if (!name) return "?";
    const parts = String(name).trim().split(/\s+/);
    return ((parts[0] ? parts[0][0] : "") +
        (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
};

const Fact = ({ icon, label, value, action }) => (
    <div className="wh-fact">
        <span className="wh-fact-icon">{icon}</span>
        <div className="wh-fact-text">
            <span className="wh-fact-label">{label}</span>
            <span className="wh-fact-value">{value || "--"}</span>
        </div>
        {action}
    </div>
);

const Profile = () => {

    const { fetchData } = useAxios();

    const dispatch = useDispatch();

    const navigate = useNavigate();

    const [user, setUser] = useState({});
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });
    const [errors, setErrors] = useState({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    /* inline editing for experience, which the employee maintains themselves */
    const [editingExperience, setEditingExperience] = useState(false);
    const [experienceDraft, setExperienceDraft] = useState("");
    const [savingExperience, setSavingExperience] = useState(false);

    const [photoOpen, setPhotoOpen] = useState(false);

    const photoUrl = user.profilePic ? downloadLink + user.profilePic : "";

    const uploadPhoto = async (file) => {
        const form = new FormData();
        form.append("photo", file, file.name);

        const resp = await fetchData({
            url: URLS.profilePicture,
            method: 'post',
            body: form
        });

        if (resp?.success === false) {
            toast.error(resp?.message || "Could not upload the picture");
            return false;
        }

        toast.success("Profile picture updated");
        /* the top bar reads this from the store, so it changes straight away */
        dispatch(setProfilePic(resp?.data?.profilePic || ""));
        getUserProfle();
        return true;
    };

    const removePhoto = async () => {
        const resp = await fetchData({ url: URLS.profilePicture, method: 'delete' });

        if (resp?.success === false) {
            toast.error(resp?.message || "Could not remove the picture");
            return;
        }

        toast.success("Profile picture removed");
        dispatch(setProfilePic(""));
        getUserProfle();
    };

    const startEditExperience = () => {
        setExperienceDraft(user.experience || "");
        setEditingExperience(true);
    };

    const saveExperience = async () => {
        setSavingExperience(true);
        /* the route is registered as POST, the same way the skills panel calls it */
        const resp = await fetchData({
            url: URLS.updateProfile,
            method: 'post',
            body: { experience: experienceDraft.trim() }
        });
        setSavingExperience(false);

        if (resp?.success === false) {
            toast.error(resp?.message || "Could not update your experience");
            return;
        }

        toast.success("Experience updated");
        setEditingExperience(false);
        getUserProfle();
    };

    const getUserProfle = async () => {
        const resp = await fetchData({ url: URLS.user, method: 'get' });
        if (resp.status === 200) {
            setUser(resp.data.employee)
        } else {
            navigate("/")
        }
    }

    const handleOpenPasswordDialog = () => {
        setOpenPasswordDialog(true);
    };

    const handleClosePasswordDialog = () => {
        setOpenPasswordDialog(false);
        setPasswordData({
            currentPassword: "",
            newPassword: "",
            confirmPassword: ""
        });
        setErrors({
            currentPassword: false,
            newPassword: false,
            confirmPassword: false
        });
        // Reset password visibility states
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData({
            ...passwordData,
            [name]: value
        });
        
        // Clear error when user types
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: false
            });
        }
    };

    const validatePasswordData = () => {
        const newErrors = {
            currentPassword: !passwordData.currentPassword,
            newPassword: !passwordData.newPassword,
            confirmPassword: !passwordData.confirmPassword || 
                            passwordData.newPassword !== passwordData.confirmPassword
        };
        
        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error);
    };

    const updatePassword = async () => {
        if (!validatePasswordData()) {
            return;
        }

        const payload = {
            emailId: user.emailID,
            currentPassword: passwordData.currentPassword,
            newPassword: passwordData.newPassword
        };
        
        try {
            const resp = await fetchData({ url: URLS.updatePassword, method: "put", body: payload });
            if (resp.status === 200) {
                toast.success("Password updated successfully");
                handleClosePasswordDialog();
            } else {
                toast.error(resp.message || "Failed to update password");
            }
        } catch (error) {
            toast.error("An error occurred while updating password");
        }
    };

    useEffect(() => {
        getUserProfle();
    }, []);

    return (
        <div className="wh-table wh-profile-page">
            <div className="wh-profile-layout">

                <aside className="wh-identity-card">
                    <div className="wh-identity-top">
                        <button type="button" className="wh-avatar-edit"
                            aria-label="Change profile picture"
                            onClick={() => setPhotoOpen(true)}>
                            {photoUrl
                                ? <img className="wh-avatar wh-avatar-lg" src={photoUrl} alt={user.employeeName} />
                                : <span className="wh-avatar wh-avatar-lg">{initials(user.employeeName)}</span>}
                            <span className="wh-avatar-badge"><PhotoCameraOutlinedIcon /></span>
                        </button>
                        <h5 title={user.employeeName}>{user.employeeName || "--"}</h5>
                        <p>{user.designation || "Designation not set"}</p>
                        <span className="wh-pill">{user.employeeCode}</span>
                    </div>

                    <ul className="wh-record-meta wh-identity-meta">
                        {user.emailID && (
                            <li><AlternateEmailOutlinedIcon /><span title={user.emailID}>{user.emailID}</span></li>
                        )}
                        {user.mobile && (
                            <li><LocalPhoneOutlinedIcon /><span>{user.countryCode} {user.mobile}</span></li>
                        )}
                        {user.gender && (
                            <li><WcOutlinedIcon /><span>{user.gender}</span></li>
                        )}
                        {user.address && (
                            <li><PlaceOutlinedIcon /><span title={user.address}>{user.address}</span></li>
                        )}
                    </ul>

                    <Button variant="outlined" startIcon={<LockOutlinedIcon />}
                        onClick={handleOpenPasswordDialog} fullWidth>
                        Update Password
                    </Button>
                </aside>

                <section className="wh-profile-main">
                    <div className="wh-panel">
                        <h6 className="wh-section-title" style={{ marginTop: 0 }}>
                            <BadgeOutlinedIcon />Professional Details
                        </h6>

                        <div className="wh-facts-grid">
                            <Fact icon={<BadgeOutlinedIcon />} label="Employee ID" value={user.employeeCode} />
                            <Fact icon={<WorkOutlineOutlinedIcon />} label="Designation" value={user.designation} />
                            <Fact icon={<ApartmentOutlinedIcon />} label="Department" value={user.department} />
                            <Fact icon={<BusinessOutlinedIcon />} label="Company" value={user.companyName} />
                            <Fact icon={<SupervisorAccountOutlinedIcon />} label="Manager" value={user.managerName} />
                            <Fact icon={<AlternateEmailOutlinedIcon />} label="Manager Email" value={user.managerEmail} />
                            <Fact icon={<CakeOutlinedIcon />} label="Date of Birth"
                                value={user.dob ? String(user.dob).substring(0, 10) : ""} />
                            <Fact icon={<PlaceOutlinedIcon />} label="Work Location" value={user.workLocation} />

                            {/* the one field on this panel the employee maintains */}
                            <div className="wh-fact wh-fact-editable">
                                <span className="wh-fact-icon"><TimelineOutlinedIcon /></span>
                                <div className="wh-fact-text">
                                    <span className="wh-fact-label">Experience</span>
                                    {editingExperience ? (
                                        <TextField
                                            value={experienceDraft}
                                            onChange={(e) => setExperienceDraft(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveExperience();
                                                if (e.key === 'Escape') setEditingExperience(false);
                                            }}
                                            placeholder="6 Years"
                                            size="small"
                                            autoFocus
                                            fullWidth
                                        />
                                    ) : (
                                        <span className="wh-fact-value">{user.experience || "Not set"}</span>
                                    )}
                                </div>

                                {editingExperience ? (
                                    <div className="wh-fact-actions">
                                        <Tooltip title="Save" placement="top" arrow>
                                            <span>
                                                <IconButton onClick={saveExperience} disabled={savingExperience}>
                                                    <CheckRoundedIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Cancel" placement="top" arrow>
                                            <IconButton className="wh-danger" onClick={() => setEditingExperience(false)}>
                                                <CloseRoundedIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                ) : (
                                    <Tooltip title="Edit experience" placement="top" arrow>
                                        <IconButton onClick={startEditExperience}>
                                            <EditOutlinedIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    </div>

                    <UserProfileSkills user={user} reloadProfile={getUserProfle} />
                    <UserAssetsDetails assets={user?.Assets || []} />
                </section>

            </div>

            <ProfilePhotoModal
                show={photoOpen}
                currentUrl={photoUrl}
                onClose={() => setPhotoOpen(false)}
                onUpload={uploadPhoto}
                onRemove={removePhoto}
            />

            {/* Password Update Dialog */}
            <Dialog open={openPasswordDialog} onClose={handleClosePasswordDialog}
                className="wh-dialog" fullWidth maxWidth="xs">
                <div className="wh-dialog-head">
                    <div className="wh-dialog-headings">
                        <h5>Update Password</h5>
                        <p>Enter your current password, then the new one</p>
                    </div>
                    <IconButton className="wh-dialog-close" onClick={handleClosePasswordDialog} aria-label="Close">
                        <CloseRoundedIcon />
                    </IconButton>
                </div>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        name="currentPassword"
                        label="Current Password"
                        type={showCurrentPassword ? "text" : "password"}
                        fullWidth
                        variant="outlined"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        error={errors.currentPassword}
                        helperText={errors.currentPassword ? "Current password is required" : ""}
                        InputProps={{
                            endAdornment: (
                                <IconButton className="wh-eye" size="small"
                                    aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                                    {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            )
                        }}
                    />
                    <TextField
                        margin="dense"
                        name="newPassword"
                        label="New Password"
                        type={showNewPassword ? "text" : "password"}
                        fullWidth
                        variant="outlined"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        error={errors.newPassword}
                        helperText={errors.newPassword ? "New password is required" : ""}
                        InputProps={{
                            endAdornment: (
                                <IconButton className="wh-eye" size="small"
                                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                                    onClick={() => setShowNewPassword(!showNewPassword)}>
                                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            )
                        }}
                    />
                    <TextField
                        margin="dense"
                        name="confirmPassword"
                        label="Confirm New Password"
                        type={showConfirmPassword ? "text" : "password"}
                        fullWidth
                        variant="outlined"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        error={errors.confirmPassword}
                        helperText={errors.confirmPassword ? 
                            passwordData.confirmPassword ? "Passwords do not match" : "Confirm password is required" 
                            : ""}
                        InputProps={{
                            endAdornment: (
                                <IconButton className="wh-eye" size="small"
                                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            )
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="inherit" onClick={handleClosePasswordDialog}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={updatePassword}>
                        Update Password
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default Profile